/**
 * Shows/hides cr-bespoke-colour-field.liquid's text input when the watched
 * option changes. Reads Horizon's picker in either style: button/radio inputs
 * carry `data-option-name`, dropdowns are named `options[<name>]`. Disabled
 * inputs are excluded from form submission, so toggling `disabled` keeps the
 * property out of the cart when the field isn't relevant.
 */
function currentOptionValue(scope, optionName) {
  const radios = scope.querySelectorAll('variant-picker input[type="radio"][data-option-name]');
  for (const radio of radios) {
    if (radio instanceof HTMLInputElement && radio.dataset.optionName === optionName && radio.checked) {
      return radio.value;
    }
  }
  const select = scope.querySelector(`variant-picker select[name="${CSS.escape(`options[${optionName}]`)}"]`);
  return select instanceof HTMLSelectElement ? select.value : undefined;
}

/**
 * Finish names are compared ignoring case, a "2026" list reference and repeated spaces,
 * so renaming the finish options does not break the trigger.
 * @param {string} name
 */
function normalise(name) {
  return name.toLowerCase().replace(/2026/g, '').replace(/\s+/g, ' ').trim();
}

function syncField(field) {
  const { optionName, triggerValue } = field.dataset;
  const input = field.querySelector('.cr-bespoke-colour-field__input');
  if (!optionName || !triggerValue || !(input instanceof HTMLInputElement)) return;

  const scope = field.closest('.shopify-section') ?? document;
  const value = currentOptionValue(scope, optionName);
  if (value === undefined) return;

  const triggers = triggerValue.split('|').map(normalise).filter(Boolean);
  const isActive = triggers.includes(normalise(value));
  field.hidden = !isActive;
  input.disabled = !isActive;
  input.required = isActive;
  if (!isActive) input.value = '';
}

function syncAllFields() {
  document.querySelectorAll('.cr-bespoke-colour-field').forEach(syncField);
}

document.addEventListener('change', (event) => {
  if (event.target instanceof Element && event.target.closest('variant-picker')) syncAllFields();
});
document.addEventListener('cr:product-synced', syncAllFields);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncAllFields);
} else {
  syncAllFields();
}

/**
 * Colour picker: shows the finish gallery from the Finishes & RAL colours page, so the
 * client keeps one list of colours (edited in the theme editor on that page). Choosing a
 * colour writes its name into the bespoke colour box, which is saved with the order.
 */
const galleryCache = new Map();

/** @param {string} url */
async function loadGallery(url) {
  if (!galleryCache.has(url)) {
    galleryCache.set(
      url,
      fetch(url)
        .then((response) => (response.ok ? response.text() : ''))
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          /** @type {{ heading: string, items: { name: string, image: string }[] }[]} */
          const groups = [];
          doc.querySelectorAll('.cr-finish-gallery__group, .cr-finish-gallery__tile').forEach((node) => {
            if (node.classList.contains('cr-finish-gallery__group')) {
              groups.push({ heading: node.textContent?.trim() ?? '', items: [] });
              return;
            }
            const name = node.querySelector('.cr-finish-gallery__name')?.textContent?.trim();
            const img = node.querySelector('img');
            if (!name) return;
            if (groups.length === 0) groups.push({ heading: '', items: [] });
            groups[groups.length - 1].items.push({ name, image: img?.getAttribute('src') ?? '' });
          });
          return groups.filter((group) => group.items.length > 0);
        })
        .catch(() => [])
    );
  }
  return galleryCache.get(url);
}

/** @param {HTMLElement} field */
async function openPicker(field, opener) {
  const dialog = field.querySelector('.cr-bespoke-colour-field__picker');
  const grid = field.querySelector('[data-cr-bespoke-grid]');
  const input = field.querySelector('.cr-bespoke-colour-field__input');
  if (!(dialog instanceof HTMLDialogElement) || !(grid instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;

  dialog.showModal();
  if (grid.childElementCount > 0) return;

  grid.textContent = 'Loading colours…';
  const groups = await loadGallery(opener.dataset.finishesUrl || '/pages/finishes-ral-colours');
  grid.textContent = '';
  if (groups.length === 0) {
    grid.textContent = 'The colour list could not be loaded. Please type your colour in the box instead.';
    return;
  }

  for (const group of groups) {
    if (group.heading) {
      const heading = document.createElement('h3');
      heading.className = 'cr-bespoke-colour-field__picker-group';
      heading.textContent = group.heading;
      grid.append(heading);
    }
    const list = document.createElement('ul');
    list.className = 'cr-bespoke-colour-field__picker-list';
    list.setAttribute('role', 'list');
    for (const item of group.items) {
      const li = document.createElement('li');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cr-bespoke-colour-field__swatch';
      button.dataset.colourName = item.name;
      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = '';
        img.loading = 'lazy';
        button.append(img);
      }
      button.append(document.createTextNode(item.name));
      li.append(button);
      list.append(li);
    }
    grid.append(list);
  }
}

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const opener = target.closest('[data-cr-bespoke-browse]');
  if (opener instanceof HTMLElement) {
    const field = opener.closest('.cr-bespoke-colour-field');
    if (field instanceof HTMLElement) openPicker(field, opener);
    return;
  }

  const dialog = target.closest('.cr-bespoke-colour-field__picker');
  if (!(dialog instanceof HTMLDialogElement)) return;

  if (target.closest('[data-cr-bespoke-close]')) {
    dialog.close();
    return;
  }

  const swatch = target.closest('.cr-bespoke-colour-field__swatch');
  if (swatch instanceof HTMLElement) {
    const input = dialog.closest('.cr-bespoke-colour-field')?.querySelector('.cr-bespoke-colour-field__input');
    if (input instanceof HTMLInputElement) {
      input.value = swatch.dataset.colourName ?? '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      dialog.close();
      input.focus();
    }
  }
});
